import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SECTIONS, META_FIELDS, getSection, PARTS } from "@/data/workbook";
import { overallProgress, sectionProgress } from "@/lib/progress";
import { nextPhotoId } from "@/lib/photos";
import SectionNav from "@/components/SectionNav";
import SectionRenderer from "@/components/SectionRenderer";
import ProgressRing from "@/components/ProgressRing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Loader2, ChevronLeft, ChevronRight, Menu, ArrowLeft, FileText, Check, Cloud, CloudOff, Boxes, Upload, Download, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function SurveyEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [activeId, setActiveId] = useState("overview");
  const [navOpen, setNavOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    Promise.all([api.getSurvey(id), api.listPhotos(id)])
      .then(([s, p]) => { setSurvey(s); setPhotos(p); setShowFile(s.show_file || null); })
      .catch(() => { toast.error("Survey not found"); nav("/"); });
  }, [id, nav]);

  const [showFile, setShowFile] = useState(null);
  const [showBusy, setShowBusy] = useState(false);
  const showRef = useRef(null);
  const uploadShow = async (file) => {
    if (!file) return;
    setShowBusy(true);
    try {
      const meta = await api.uploadShowFile(id, (() => { const fd = new FormData(); fd.append("file", file); return fd; })());
      setShowFile(meta); toast.success("Show file uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setShowBusy(false); if (showRef.current) showRef.current.value = ""; }
  };
  const downloadShow = async () => {
    try {
      const res = await api.downloadShowFile(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = showFile?.filename || "showfile"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const removeShow = async () => { try { await api.deleteShowFile(id); setShowFile(null); toast.success("Removed"); } catch { toast.error("Failed"); } };

  const scheduleSave = (next) => {
    setSaving(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.updateSurvey(id, { title: next.title, meta: next.meta, data: next.data });
      } catch { toast.error("Auto-save failed"); }
      finally { setSaving(false); }
    }, 700);
  };

  const patchSurvey = (updater) => {
    setSurvey((prev) => {
      const next = updater(prev);
      scheduleSave(next);
      return next;
    });
  };

  const setMeta = (k, v) => patchSurvey((p) => ({ ...p, meta: { ...p.meta, [k]: v } }));
  const setTitle = (v) => patchSurvey((p) => ({ ...p, title: v }));

  const onBlockChange = (sectionId, blockKey, value) => {
    patchSurvey((p) => ({
      ...p,
      data: { ...p.data, [sectionId]: { ...(p.data?.[sectionId] || {}), [blockKey]: value } },
    }));
  };

  const capturePhoto = async (file, meta = {}) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("photo_id", nextPhotoId(photos));
      fd.append("area", meta.area || "");
      fd.append("view", meta.view || "");
      fd.append("notes", meta.notes || "");
      const rec = await api.uploadPhoto(id, fd);
      setPhotos((p) => [...p, rec]);
      toast.success(`${rec.photo_id} captured`);
      return rec;
    } catch {
      toast.error("Capture failed");
      return null;
    }
  };

  const overall = useMemo(() => (survey ? overallProgress(survey.data, photos.length) : 0), [survey, photos.length]);

  const [genBusy, setGenBusy] = useState(false);
  const generateAssets = async () => {
    setGenBusy(true);
    try {
      const r = await api.generateAssets(id);
      if (r.created) toast.success(`${r.created} asset${r.created > 1 ? "s" : ""} added to inventory${r.skipped ? ` (${r.skipped} already existed)` : ""}`);
      else toast.info("No new fixtures or speakers found to add. Fill Section 8 / 15 first.");
    } catch {
      toast.error("Could not generate assets");
    } finally {
      setGenBusy(false);
    }
  };

  const goToSection = (sid) => {
    setActiveId(sid);
    setNavOpen(false);
    if (contentRef.current) contentRef.current.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!survey) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const idx = SECTIONS.findIndex((s) => s.id === activeId);
  const prevSection = activeId === "overview" ? null : (idx <= 0 ? "overview" : SECTIONS[idx - 1].id);
  const nextSection = activeId === "overview" ? SECTIONS[0].id : (idx >= SECTIONS.length - 1 ? null : SECTIONS[idx + 1].id);

  const section = activeId === "overview" ? null : getSection(activeId);
  const partMeta = section ? PARTS.find((p) => p.id === section.part) : null;

  const NavPanel = <SectionNav data={survey.data} photoCount={photos.length} activeId={activeId} onSelect={goToSection} />;

  return (
    <div className="relative z-10 min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl no-print">
        <div className="flex h-16 items-center gap-3 px-3 sm:px-6">
          <Button variant="ghost" size="icon" onClick={() => nav("/surveys")} className="shrink-0" data-testid="back-to-dashboard"><ArrowLeft className="h-5 w-5" /></Button>
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="shrink-0 lg:hidden" data-testid="open-nav"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto p-4">{NavPanel}</SheetContent>
          </Sheet>
          <div className="min-w-0 flex-1">
            <Input data-testid="survey-title-input" value={survey.title} onChange={(e) => setTitle(e.target.value)} className="h-9 border-none bg-transparent px-0 text-base font-semibold focus-visible:ring-0" />
            <div className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
              {saving ? <><CloudOff className="h-3 w-3" /> Saving…</> : <><Cloud className="h-3 w-3 text-emerald-500" /> Saved</>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <div className="hidden items-center gap-2 sm:flex">
              <ProgressRing value={overall} size={38} />
              <span className="text-xs text-muted-foreground">complete</span>
            </div>
            <Button data-testid="generate-assets-btn" onClick={generateAssets} disabled={genBusy} variant="outline" className="gap-2 tap-scale">
              {genBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Boxes className="h-4 w-4" />} <span className="hidden sm:inline">To assets</span>
            </Button>
            <Button data-testid="open-report-btn" onClick={() => nav(`/survey/${id}/report`)} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
              <FileText className="h-4 w-4" /> <span className="hidden sm:inline">Report</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[100rem] gap-0">
        {/* Sidebar desktop */}
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-72 shrink-0 overflow-y-auto border-r border-border p-4 lg:block no-print">
          {NavPanel}
        </aside>

        {/* Content */}
        <main ref={contentRef} className="min-w-0 flex-1 px-4 py-6 sm:px-8 sm:py-8">
          <div className="mx-auto max-w-3xl">
            {activeId === "overview" ? (
              <div className="space-y-5">
                <div>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Survey details</span>
                  <h2 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Overview</h2>
                  <p className="mt-2 text-sm text-muted-foreground">Record what is there — before touching anything.</p>
                </div>
                <div className="rounded-xl border border-border bg-card/60 p-5">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {META_FIELDS.map((f) => (
                      <div key={f.k}>
                        <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">{f.label}</Label>
                        <Input
                          data-testid={`meta-${f.k}`}
                          type={f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
                          value={survey.meta?.[f.k] || ""}
                          onChange={(e) => setMeta(f.k, e.target.value)}
                          className="bg-secondary/40"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card/60 p-5">
                  <div className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-foreground"><Paperclip className="h-4 w-4 text-primary" /> Baseline show file</div>
                  <p className="mb-3 text-xs text-muted-foreground">Upload the venue console baseline (e.g. Mantra show export). Anyone can download it later.</p>
                  <input ref={showRef} type="file" hidden onChange={(e) => uploadShow(e.target.files?.[0])} />
                  {showFile ? (
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 p-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-foreground">{showFile.filename}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{Math.round((showFile.size || 0) / 1024)} KB · {new Date(showFile.uploaded_at).toLocaleDateString()}</div>
                      </div>
                      <div className="flex shrink-0 gap-1.5">
                        <Button data-testid="download-showfile-btn" onClick={downloadShow} variant="outline" size="sm" className="gap-1.5 tap-scale"><Download className="h-4 w-4" /> Download</Button>
                        <Button onClick={removeShow} variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ) : (
                    <Button data-testid="upload-showfile-btn" onClick={() => showRef.current?.click()} disabled={showBusy} variant="outline" className="gap-2 border-dashed tap-scale">
                      {showBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload show file
                    </Button>
                  )}
                </div>

              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 items-center rounded-md px-2 font-mono text-[11px] font-bold" style={{ background: partMeta.color, color: "#0B0F17" }}>PART {section.part} · {section.num}</span>
                    {sectionProgress(section, survey.data, photos.length) === 100 && <span className="flex items-center gap-1 text-xs text-emerald-500"><Check className="h-4 w-4" /> Complete</span>}
                  </div>
                  <h2 className="mt-2 text-2xl font-bold leading-tight tracking-tight sm:text-3xl">{section.title}</h2>
                </div>
                <SectionRenderer
                  section={section}
                  sdata={survey.data?.[section.id]}
                  onBlockChange={(blockKey, value) => onBlockChange(section.id, blockKey, value)}
                  surveyId={id}
                  photos={photos}
                  setPhotos={setPhotos}
                  onCapturePhoto={capturePhoto}
                />
              </div>
            )}

            {/* Prev / Next */}
            <div className="mt-8 flex items-center justify-between gap-3 no-print">
              <Button data-testid="prev-section-btn" variant="outline" disabled={!prevSection} onClick={() => prevSection && goToSection(prevSection)} className="gap-2 tap-scale">
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <Button data-testid="next-section-btn" variant="outline" disabled={!nextSection} onClick={() => nextSection && goToSection(nextSection)} className="gap-2 tap-scale">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
